<template>
  <div class="p-4">
    <u-error-handling
      v-if="errorObject"
      :error-object="errorObject"
      class="mb-4"
    />
    <b-row class="mb-4">
      <b-col cols="2">
        <v-select
          v-model="request.yearFrom"
          :items="yearOptions"
          label="Von"
          menu-props="auto"
        />
      </b-col>
      <b-col cols="2">
        <v-select
          v-model="request.yearTo"
          :items="yearOptions"
          label="Bis"
          menu-props="auto"
        />
      </b-col>
      <b-col cols="2">
        <v-autocomplete
          v-model="request.pathFilter"
          :items="pathFilterOptions"
          item-text="text"
          item-value="path"
          label="Ort"
          menu-props="auto"
          clearable
          :loading="isLoadingOptions"
        />
      </b-col>
    </b-row>
    <v-flex
      v-if="isLoading"
      justify-center
    >
      <u-loader />
    </v-flex>
    <div
      v-else
      class="calenderOverview-wrapper"
    >
      <table class="w-100 calenderOverview">
        <thead>
          <tr>
            <th>Jahr</th>
            <th>Kanton</th>
            <th>Bezirk</th>
            <th>Ort</th>
            <th>Total</th>
            <th>Mo - Fr</th>
            <th>Sa</th>
            <th>So</th>
            <th v-if="hasConfigurationId">
              Zusätzliche
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(entry, index) in entries"
            :key="`${entry.year}-${entry.kanton}-${entry.ort}-${index}`"
          >
            <td>{{ entry.year }}</td>
            <td>{{ entry.kanton }}</td>
            <td>{{ entry.bezirk }}</td>
            <td>
              <div
                v-for="(line, lineIndex) in formatOrt(entry.ort)"
                :key="`${entry.year}-${index}-ort-${lineIndex}`"
              >
                {{ line }}
              </div>
            </td>
            <td>{{ evalFeiertagValue(entry,'Total') }}</td>
            <td :class="{ 'working-day': Number(entry.workingDayType) >= 1, 'ignored': Number(entry.workingDayType) < 1 && hasConfigurationId }">
              {{ evalFeiertagValue(entry,'MoFr') }}
            </td>
            <td :class="{ 'working-day': Number(entry.workingDayType) >= 2, 'ignored': Number(entry.workingDayType) < 2 && hasConfigurationId}">
              {{ evalFeiertagValue(entry,'Sa') }}
            </td>
            <td :class="{ 'working-day': Number(entry.workingDayType) >= 3, 'ignored': Number(entry.workingDayType) < 3 && hasConfigurationId}">
              {{ evalFeiertagValue(entry,'So') }}
            </td>
            <td v-if="hasConfigurationId">
              {{ entry.configAdd }}
            </td>
          </tr>
          <tr v-if="!entries.length">
            <td
              class="text-center"
              :colspan="hasConfigurationId ? 13 : 8"
            >
              Keine Daten vorhanden
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { GetDashboardEntries } from "@/services/publicholidays.service";
import { GetCitiesVNext } from "@/services/contract.edition.cantons.service.js";
import { GetAllCities } from "@/services/cantons.service.js";
export default {
  props: {
    presetConfigurationId: {
      type: Number,
      default: null,
    },
  },
  data() {
    const year = new Date().getFullYear();
    const yearOptions = [];

    for (let i = year - 10; i <= year + 10; i += 1) {
      yearOptions.push(i);
    }

    return {
      entries: [],
      isLoading: false,
      isLoadingOptions: false,
      errorObject: null,
      isAdjustingYearRange: false,
      yearOptions,
      pathFilterOptions: [],
      request: {
        yearFrom: year,
        yearTo: year,
        language: "de-CH",
        configurationId: this.presetConfigurationId,
        pathFilter: null,
      },
    };
  },
  computed: {
    hasConfigurationId() {
      return (
        this.request.configurationId !== null &&
        this.request.configurationId !== undefined &&
        this.request.configurationId !== ""
      );
    },
  },
  watch: {
    presetConfigurationId(newValue) {
      this.request.configurationId = newValue;
    },
    "request.yearFrom"() {
      this.onYearRangeChanged("from");
    },
    "request.yearTo"() {
      this.onYearRangeChanged("to");
    },
    "request.pathFilter"() {
      this.onYearRangeChanged("pathFilter");
    },
  },
  created() {
    this.loadDashboardEntries();
    this.loadPathFilterOptions();
  },
  methods: {
    onYearRangeChanged(changedField) {
      if (this.isAdjustingYearRange) {
        return;
      }

      const yearFrom = Number(this.request.yearFrom);
      const yearTo = Number(this.request.yearTo);

      if (Number.isNaN(yearFrom) || Number.isNaN(yearTo)) {
        return;
      }

      if (yearFrom > yearTo) {
        this.isAdjustingYearRange = true;

        if (changedField === "from") {
          this.request.yearTo = yearFrom;
        } else {
          this.request.yearFrom = yearTo;
        }

        this.$nextTick(() => {
          this.isAdjustingYearRange = false;
          this.loadDashboardEntries();
        });

        return;
      }

      this.loadDashboardEntries();
    },
    formatOrt(value) {
      if (!value) {
        return [];
      }

      return String(value)
        .split(/(?=\d{4}\b)/g)
        .map((part) => part.trim())
        .filter((part) => part.length > 0);
    },
    getMoFrTotal(entry) {
      return Number(entry.globMoFr || 0) + Number(entry.configMoFr || 0);
    },
    getSaTotal(entry) {
      return Number(entry.globSa || 0) + Number(entry.configSa || 0);
    },
    getSoTotal(entry) {
      return Number(entry.globSo || 0) + Number(entry.configSo || 0);
    },
    evalFeiertagValue(entry, part) {
      switch (part) {
        case "MoFr":
          switch (entry.workingDayType) {
            case 0:
              return "?";
            case 1:
            case 2:
            case 3:
              return entry.configMoFr !== 0
                ? `${this.getMoFrTotal(entry)} (${entry.configMoFr})`
                : `${this.getMoFrTotal(entry)}`;
            default:
              return entry.globMoFr;
          }

        case "Sa":
          switch (entry.workingDayType) {
            case 0:
              return "?";
            case 2:
            case 3:
                  return entry.configSa !== 0 ? `${this.getSaTotal(entry)} (${entry.configSa})` : `${this.getSaTotal(entry)}`;
            default:
              return entry.globSa;
          }

        case "So":
          switch (entry.workingDayType) {
            case 0:
              return "?";
            case 3:
                  return entry.configSo !== 0 ? `${this.getSoTotal(entry)} (${entry.configSo})` : `${this.getSoTotal(entry)}`;
            default:
              return entry.globSo;
          }

        case "Total":
          switch (entry.workingDayType) {
            case 0:
              return "?";
            case 1:
                  return `${this.getMoFrTotal(entry) + entry.configAdd}`;
            case 2:
                  return `${this.getMoFrTotal(entry) + this.getSaTotal(entry) + entry.configAdd}`;
            case 3:
                  return `${this.getMoFrTotal(entry) + this.getSaTotal(entry) + this.getSoTotal(entry) + entry.configAdd}`;
            default:
              return entry.anzahlFeiertage;
          }

        default:
          return "?";
      }
    },
    loadDashboardEntries() {
      this.isLoading = true;
      this.errorObject = null;

      GetDashboardEntries(
        {
          ...this.request,
          validFrom: `${this.request.yearFrom || new Date().getFullYear()}-01-01`,
          validTo: `${this.request.yearTo || new Date().getFullYear()}-12-31`,
        },
        (response) => {
          const items = Array.isArray(response) ? response : [];
          this.entries = items.map((entry) => ({
            year: entry.jahr,
            kanton: entry.kanton,
            bezirk: entry.bezirk,
            ort: entry.ort,
            anzahlFeiertage: entry.anzahlFeiertage,
            globMoFr: entry.globMoFr,
            globSa: entry.globSa,
            globSo: entry.globSo,
            configMoFr: entry.configMoFr,
            configSa: entry.configSa,
            configSo: entry.configSo,
            configAdd: entry.configAdd,
            workingDayType: entry.workingDayType,
          }));
          this.isLoading = false;
        },
        (error) => {
          this.errorObject = error;
          this.isLoading = false;
        },
      );
    },
    loadPathFilterOptions() {
      this.isLoadingOptions = true;
      this.errorObject = null;

      if(this.presetConfigurationId){
        GetCitiesVNext(
        this.$store.state.mindestLohnKonfiguration.BasicContractInformation.contractNumber,
        this.$store.state.mindestLohnKonfiguration.BasicContractInformation.contractVersion,
        this.$store.state.mindestLohnKonfiguration.Configuration.editionId,
        (response) => {
          this.pathFilterOptions = response.map((x) => {
            return {
              id: x.cityId,
              text: x.cityValue,
              path: x.path,
            };
          });
          this.isLoadingOptions = false;
        },
        (error) => {
          this.PlzErrorObject = error;
          this.isLoadingOptions = false;
        },
      );
      }
      else {
        GetAllCities(
          (response) => {
            this.pathFilterOptions = response.map((x) => {
              return {
                id: x.cityId,
                text: `${x.plz} ${x.city} (${x.district} ${x.cantonCode})`,
                path: x.path
              };
            });
            this.isLoadingOptions = false;
          },
          (error) => {
            this.PlzErrorObject = error;
            this.isLoadingOptions = false;
          },
        );
      }

    }
  },
};
</script>

<style scoped>
.calenderOverview {
  font-size: small;
  border-collapse: collapse;
}

.calenderOverview td,
.calenderOverview th {
  padding: 8px 4px;
  vertical-align: top;
  border-bottom: 1px solid #d9d9d9;
}

.calenderOverview-wrapper {
  max-height: 70vh;
  overflow-y: auto;
}

.calenderOverview thead th {
  position: sticky;
  top: 0px;
  z-index: 1;
  background-color: #fff;
}

.calenderOverview td.working-day {
  background-color: #f3f3f3;
}

.calenderOverview td.ignored {
  text-decoration: line-through;
}
</style>
